import { ByteReader, readRange, readUint64, toSafeNumber } from './bytes'
import { MAX_COMPRESSED_HEADER, MAX_UNPACKED_HEADER } from './constants'
import { ArchiveError } from './errors'
import type { PhotoRef } from './imageIndex'
import { decodeLzmaHeader } from './lzmaHeader'

const K_END = 0x00n
const K_HEADER = 0x01n
const K_MAIN_STREAMS = 0x04n
const K_FILES_INFO = 0x05n
const K_PACK_INFO = 0x06n
const K_UNPACK_INFO = 0x07n
const K_SUBSTREAMS_INFO = 0x08n
const K_SIZE = 0x09n
const K_CRC = 0x0an
const K_FOLDER = 0x0bn
const K_CODERS_UNPACK_SIZE = 0x0cn
const K_NUM_UNPACK_STREAM = 0x0dn
const K_EMPTY_STREAM = 0x0en
const K_NAME = 0x11n
const K_ENCODED_HEADER = 0x17n
const CODER_COPY = '00'
const CODER_LZMA = '030101'
const SEVENZ_MAGIC = [0x37, 0x7a, 0xbc, 0xaf, 0x27, 0x1c]

type Folder = {
  coders: { id: string; props: Uint8Array }[]
  numOut: number
  numPack: number
  crcDefined: boolean
  unpackSizes: bigint[]
}

const skipDigests = (reader: ByteReader, count: number): boolean[] => {
  const defined = reader.boolVector(count, 4)
  defined.forEach(isDefined => {
    if (isDefined) reader.uint32()
  })
  return defined
}

const readPackInfo = (reader: ByteReader): { packPos: bigint; packSizes: bigint[] } => {
  const packPos = reader.number()
  const count = reader.count()
  let packSizes: bigint[] = []
  for (;;) {
    const id = reader.number()
    if (id === K_END) break
    if (id === K_SIZE) {
      packSizes = []
      for (let i = 0; i < count; i++) packSizes.push(reader.number())
    } else if (id === K_CRC) {
      skipDigests(reader, count)
    } else {
      throw new ArchiveError('unsupported')
    }
  }
  return { packPos, packSizes }
}

const readFolder = (reader: ByteReader): Folder => {
  const coderCount = reader.count(1)
  const coders: Folder['coders'] = []
  let totalIn = 0
  let totalOut = 0
  for (let i = 0; i < coderCount; i++) {
    const flags = reader.byte()
    const id = [...reader.take(flags & 0x0f)].map(byte => byte.toString(16).padStart(2, '0')).join('')
    let inputs = 1
    let outputs = 1
    if (flags & 0x10) {
      inputs = reader.count()
      outputs = reader.count()
    }
    let props = new Uint8Array(0)
    if (flags & 0x20) props = Uint8Array.from(reader.take(reader.count(1)))
    totalIn += inputs
    totalOut += outputs
    coders.push({ id, props })
  }
  const bindPairs = totalOut - 1
  for (let i = 0; i < bindPairs; i++) {
    reader.number()
    reader.number()
  }
  const numPack = totalIn - bindPairs
  if (numPack > 1) {
    for (let i = 0; i < numPack; i++) reader.number()
  }
  return { coders, numOut: totalOut, numPack, crcDefined: false, unpackSizes: [] }
}

const readUnpackInfo = (reader: ByteReader): Folder[] => {
  if (reader.number() !== K_FOLDER) throw new ArchiveError('unsupported')
  const folderCount = reader.count(1)
  if (reader.byte() !== 0) throw new ArchiveError('unsupported')
  const folders: Folder[] = []
  for (let i = 0; i < folderCount; i++) folders.push(readFolder(reader))
  if (reader.number() !== K_CODERS_UNPACK_SIZE) throw new ArchiveError('unsupported')
  folders.forEach(folder => {
    folder.unpackSizes = []
    for (let i = 0; i < folder.numOut; i++) folder.unpackSizes.push(reader.number())
  })
  for (;;) {
    const id = reader.number()
    if (id === K_END) break
    if (id === K_CRC) {
      const defined = skipDigests(reader, folderCount)
      defined.forEach((isDefined, index) => {
        folders[index].crcDefined = isDefined
      })
    } else {
      throw new ArchiveError('unsupported')
    }
  }
  return folders
}

const folderSize = (folder: Folder): bigint => folder.unpackSizes[folder.unpackSizes.length - 1] ?? 0n

const readSubStreamsInfo = (reader: ByteReader, folders: Folder[]): bigint[][] => {
  let counts = folders.map(() => 1)
  const sizes: bigint[][] = []
  let id = reader.number()
  if (id === K_NUM_UNPACK_STREAM) {
    counts = folders.map(() => reader.count())
    id = reader.number()
  }
  folders.forEach((folder, index) => {
    if (!counts[index]) {
      sizes.push([])
      return
    }
    const group: bigint[] = []
    let total = 0n
    if (id === K_SIZE) {
      for (let i = 0; i < counts[index] - 1; i++) {
        const size = reader.number()
        group.push(size)
        total += size
      }
    }
    group.push(folderSize(folder) - total)
    sizes.push(group)
  })
  if (id === K_SIZE) id = reader.number()
  while (id !== K_END) {
    if (id === K_CRC) {
      let unknown = 0
      folders.forEach((folder, index) => {
        if (counts[index] === 1 && folder.crcDefined) return
        unknown += counts[index]
      })
      skipDigests(reader, unknown)
    } else {
      throw new ArchiveError('unsupported')
    }
    id = reader.number()
  }
  return sizes
}

type Streams = {
  packPos: bigint
  packSizes: bigint[]
  folders: Folder[]
  sizes: bigint[][]
}

const readStreamsInfo = (reader: ByteReader): Streams => {
  let packPos = 0n
  let packSizes: bigint[] = []
  let folders: Folder[] = []
  let sizes: bigint[][] = []
  let id = reader.number()
  if (id === K_PACK_INFO) {
    ;({ packPos, packSizes } = readPackInfo(reader))
    id = reader.number()
  }
  if (id === K_UNPACK_INFO) {
    folders = readUnpackInfo(reader)
    sizes = folders.map(folder => [folderSize(folder)])
    id = reader.number()
  }
  if (id === K_SUBSTREAMS_INFO) {
    sizes = readSubStreamsInfo(reader, folders)
    id = reader.number()
  }
  if (id !== K_END) throw new ArchiveError('unsupported')
  return { packPos, packSizes, folders, sizes }
}

const readFileNames = (reader: ByteReader, count: number, size: number): string[] => {
  const end = reader.offset + size
  if (reader.byte() !== 0) throw new ArchiveError('unsupported')
  const raw = reader.take(end - reader.offset)
  return new TextDecoder('utf-16le').decode(raw).split('\u0000').slice(0, count)
}

export const parseSevenZipHeader = (bytes: Uint8Array): PhotoRef[] => {
  const reader = new ByteReader(bytes)
  if (reader.number() !== K_HEADER) throw new ArchiveError('unsupported')
  let streams: Streams | null = null
  let id = reader.number()
  if (id === K_MAIN_STREAMS) {
    streams = readStreamsInfo(reader)
    id = reader.number()
  }
  const files: { name: string; hasStream: boolean }[] = []
  if (id === K_FILES_INFO) {
    const count = reader.count()
    let emptyStream = new Array<boolean>(count).fill(false)
    let names: string[] = []
    for (;;) {
      const prop = reader.number()
      if (prop === K_END) break
      const size = toSafeNumber(reader.number())
      const next = reader.offset + size
      if (prop === K_EMPTY_STREAM) emptyStream = reader.bits(count)
      else if (prop === K_NAME) names = readFileNames(reader, count, size)
      reader.offset = next
    }
    for (let i = 0; i < count; i++) files.push({ name: names[i] || '', hasStream: !emptyStream[i] })
  }

  const positions: { offset: number; size: number; stored: boolean }[] = []
  if (streams) {
    let packIndex = 0
    let folderOffset = 32n + streams.packPos
    streams.folders.forEach((folder, index) => {
      const stored = folder.coders.length === 1 && folder.coders[0].id === CODER_COPY
      let start = folderOffset
      for (const size of streams.sizes[index] ?? []) {
        positions.push({ offset: toSafeNumber(start), size: toSafeNumber(size), stored })
        start += size
      }
      for (let i = 0; i < folder.numPack; i++) folderOffset += streams.packSizes[packIndex++] ?? 0n
    })
  }

  const entries: PhotoRef[] = []
  let streamIndex = 0
  files.forEach(file => {
    if (!file.hasStream) return
    const spot = positions[streamIndex++]
    if (!spot) return
    entries.push({
      name: file.name,
      kind: spot.stored ? 'raw' : 'packed',
      offset: spot.offset,
      size: spot.size,
      uncompressedSize: spot.size,
      method: 0,
    })
  })
  return entries
}

export type HeaderDecoder = (
  props: Uint8Array,
  packed: Uint8Array,
  unpackedSize: number
) => Uint8Array | Promise<Uint8Array>

const decodeEncodedHeader = async (file: Blob, bytes: Uint8Array, decodeHeader: HeaderDecoder): Promise<Uint8Array> => {
  const reader = new ByteReader(bytes)
  reader.number()
  const info = readStreamsInfo(reader)
  const folder = info.folders[0]
  const coder = folder?.coders[0]
  if (!coder || coder.id !== CODER_LZMA) throw new ArchiveError('unsupported')
  const unpacked = toSafeNumber(folderSize(folder))
  const packedSize = toSafeNumber(info.packSizes[0] ?? 0n)
  if (unpacked > MAX_UNPACKED_HEADER || packedSize > MAX_COMPRESSED_HEADER) throw new ArchiveError('header-too-large')
  const start = toSafeNumber(32n + info.packPos)
  const packed = await readRange(file, start, start + packedSize, MAX_COMPRESSED_HEADER)
  return await decodeHeader(coder.props, packed, unpacked)
}

export const isSevenZip = (magic: Uint8Array): boolean => SEVENZ_MAGIC.every((byte, index) => magic[index] === byte)

export async function readSevenZipIndex(
  file: Blob,
  decodeHeader: HeaderDecoder = decodeLzmaHeader
): Promise<PhotoRef[]> {
  const headBytes = await readRange(file, 0, 32, MAX_COMPRESSED_HEADER)
  const head = new DataView(headBytes.buffer, headBytes.byteOffset, headBytes.byteLength)
  const nextOffset = readUint64(head, 12)
  const nextSize = readUint64(head, 20)
  if (nextSize === 0n) throw new ArchiveError('empty')
  if (nextSize > BigInt(MAX_COMPRESSED_HEADER)) throw new ArchiveError('header-too-large')
  const start = toSafeNumber(32n + nextOffset)
  const size = toSafeNumber(nextSize)
  let bytes = await readRange(file, start, start + size, MAX_COMPRESSED_HEADER)
  if (BigInt(bytes[0]) === K_ENCODED_HEADER) bytes = await decodeEncodedHeader(file, bytes, decodeHeader)
  return parseSevenZipHeader(bytes)
}
